import { useCallback, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/alerts/api/config";
import { useAlertAction } from "@/alerts/api/queries/alerts-workspace.queries";
import { getAlertWorkspaceDetail } from "@/alerts/api/requests/alert-workflow.requests";
import { alertWorkflowFailure } from "@/alerts/api/envelope";
import type { AlertWorkspaceDetail } from "@/alerts/types/alerts-workspace";
import { alertDecisionAction, type AlertVerdict } from "@/alerts/utils/alert-decision";
import i18n from "@/shared/lib/i18n/index.ts";

/** What went wrong, in words an operator can act on. */
export interface VerdictConflict {
  alertId: number;
  message: string;
}

/** Resolved per call rather than at import, so it follows a language change. */
const conflictMessage = (): string => i18n.t("errors.verdictConflict", { ns: "alerts" });

/**
 * Give an alert a verdict through the workflow API.
 *
 * The map used to PATCH `{ status }` at a compatibility endpoint that carries no
 * workflow version. That endpoint accepts a FIRST decision and refuses to change
 * a completed one, so corrections quietly failed — and, having no version, it
 * could not have told two operators apart anyway (MIN-270).
 *
 * Three things make a decision safe here, and each is a rule the old path broke:
 *
 * - The command is read from CANONICAL detail fetched at submit time, never from
 *   the list row on screen. A row can be minutes old; deciding from it is how one
 *   operator overwrites another's decision without either of them seeing it.
 * - `If-Match` carries the version that detail reported, so the server rejects
 *   the write if anything moved underneath it.
 * - A fresh `Idempotency-Key` per decision, reused only when retrying the SAME
 *   decision after a failure. Reusing it for a different decision would replay
 *   the old result; a new one on a retry would apply the action twice.
 *
 * Nothing is applied optimistically: the verdict on screen is the server's, not
 * a guess. A conflict is surfaced rather than retried — an alert that moved is
 * exactly the case where retrying is the wrong answer.
 */
export const useAlertVerdict = () => {
  const queryClient = useQueryClient();
  const action = useAlertAction();
  const [conflict, setConflict] = useState<VerdictConflict | null>(null);
  const [pendingFor, setPendingFor] = useState<number | null>(null);
  /** Held per alert+verdict so a retry is the same intent, not a new one. */
  const intents = useRef(new Map<string, string>());

  const submit = useCallback(
    async (alertId: number, verdict: AlertVerdict) => {
      setConflict(null);
      setPendingFor(alertId);
      try {
        // Canonical, not the row on screen. `fetchQuery` shares the detail cache
        // with everything else reading this alert.
        const detail = await queryClient.fetchQuery<AlertWorkspaceDetail>({
          queryKey: [QUERY_KEYS.ALERT_DETAIL, alertId],
          queryFn: ({ signal }) => getAlertWorkspaceDetail(alertId, signal),
          staleTime: 0,
        });

        const request = alertDecisionAction(detail.status, detail.disposition, verdict);
        // Already carries this verdict — the controls should not have offered it,
        // and submitting anyway would burn a version for no change.
        if (!request) return;

        const intentKey = `${alertId}:${verdict}`;
        const idempotencyKey = intents.current.get(intentKey) ?? crypto.randomUUID();
        intents.current.set(intentKey, idempotencyKey);

        await action.mutateAsync({
          alertId,
          action: request,
          workflowVersion: detail.workflow_version,
          idempotencyKey,
        });
        // Succeeded: this intent is spent, so the next press is a new decision.
        intents.current.delete(intentKey);
      } catch (error) {
        const failure = alertWorkflowFailure(error);
        if (
          failure === "alert_state_changed" ||
          failure === "workflow_version_required" ||
          failure === "invalid_transition"
        ) {
          // `useAlertAction` has already re-read canonical state for these.
          setConflict({ alertId, message: conflictMessage() });
        }
        // Anything else surfaces through the mutation's own error state.
      } finally {
        setPendingFor(null);
      }
    },
    [queryClient, action],
  );

  return {
    submit,
    /** The alert a decision is in flight for, or null. */
    pendingFor,
    isPending: pendingFor != null,
    conflict,
    dismissConflict: useCallback(() => setConflict(null), []),
    error: action.error,
  };
};
